import { EditNoteOutlined } from '@mui/icons-material'
import type { TooltipProps } from '@mui/material'
import { Box, IconButton, Stack, TextField, Tooltip, tooltipClasses } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import { DiffViewer } from '@/features/controller/configuration/components/textEditor/DiffViewer'
import type { UnsavedConfiguration } from '@/features/controller/configuration/models/controllerConfiguration'
import type { ControllerConfigVersion } from '@/features/controller/configuration/types'
import { getControllerConfigType } from '@/features/controller/configuration/utils/controllerConfigTypes'

const NoMaxWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 'none',
  },
})

interface UnsavedEditButtonProps {
  controllerId: string
  previousConfigVersion: ControllerConfigVersion
  unsavedChange: UnsavedConfiguration
}

export function UnsavedEditButton({
  controllerId,
  previousConfigVersion,
  unsavedChange,
}: Readonly<UnsavedEditButtonProps>) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const configType = getControllerConfigType(previousConfigVersion.configurationTypeId)

  function openAsProposedEdit() {
    navigate(configType.navigationPath(controllerId), {
      state: {
        proposedEdit: {
          previousVersionId: previousConfigVersion.id,
          value: unsavedChange.value,
          comment: unsavedChange.comment,
        },
      },
    })
  }

  const buttonSize = '42px'

  return (
    <NoMaxWidthTooltip
      arrow={true}
      disableFocusListener={true}
      placement="left"
      slotProps={{ tooltip: { style: { backgroundColor: 'white' } }, arrow: { style: { color: 'white' } } }}
      title={
        <Box sx={{ width: '55vw' }}>
          <CustomTypography padding={1} sx={{ color: 'black' }} variant="h5">
            {t('controllers.configuration.tree.unsaved_changes.title', { configTypeName: configType.name })}
          </CustomTypography>
          <DiffViewer
            language={configType.textEditorLanguage}
            modifiedText={unsavedChange.value}
            originalText={previousConfigVersion.value}
          />
          <Stack padding={1}>
            <CustomTypography variant="caption">{t('controllers.configuration.comment')}</CustomTypography>
            <TextField
              multiline={true}
              value={unsavedChange.comment}
              onChange={(e) => unsavedChange.setComment(e.target.value)}
            />
          </Stack>
        </Box>
      }
    >
      <Stack alignItems="center" direction="row">
        <IconButton
          color="info"
          component={EditNoteOutlined}
          sx={{ boxShadow: 'none', width: buttonSize, height: buttonSize }}
          onClick={openAsProposedEdit}
        />
        <CustomTypography color="info">Pending {configType.veryShortName} change</CustomTypography>
      </Stack>
    </NoMaxWidthTooltip>
  )
}
